import { useRouter } from "next/router";
import { selectSingleScrollHomeScreen } from "../../../../services/redux/selectors/homeScreenSelectors";
import { useLoyaltyOffersPopupOpener } from "../../../../services/hooks/loyalty/useLoyaltyOffersPopupOpener";
import { usePayWithPoints } from "../../../../services/hooks/loyalty/usePayWithPoints";
import { useCustomerAnalytics } from "../../../../services/hooks/useCustomerAnalytics";
import { useLoyaltyUiModel } from "../../../../services/hooks/useLoyaltyUiModel";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../../services/hooks/useTranslationI18";
import {
  selectGuestMilestonesEnabled,
  selectGuestTiersEnabled,
  selectHasAnyTierMilestones,
} from "../../../../services/redux/selectors/loyaltyServiceSelectors";
import {
  setBottomSheetState,
  updateCurrentHomeScreenTab,
} from "../../../../services/redux/slices/globalComponentsToggleSlice";
import {
  CUSTOMER_ANALYTICS_EVENT,
  PagesEnum,
  TabsEnum,
} from "../../../../utils/enums";
import {
  localizedNumber,
  returnSingularOrPlural,
} from "../../../../utils/help/localization/localization";
import { getPageLink } from "../../../../utils/help/navigation/navigation";
import ConfettiComponent from "../../../global/AnimatedConffeti/AnimatedConfettiComponent";
import {
  MileStone,
  PointsArrowIcon,
} from "../../../global/Icons/loyalty.icons";
import { PayWithPointsBanner } from "../../../global/PayWithPoints/PayWithPointsBanner";
import {
  getCurrentLoyaltyTier,
  getTierColor,
  sortLoyaltyTiers,
  toTierCssProperties,
} from "../../../../utils/loyalty/tiers";
import { useAuth } from "../../../Layout/auth/useAuth";
import { LoyaltyPointsBalance } from "../LoyaltyPointsBalance/LoyaltyPointsBalance";
import { LoyaltyPointsExpiryBanner } from "../LoyaltyPointsExpiryBanner/LoyaltyPointsExpiryBanner";
import { LoyaltyPointsProgressBar } from "../LoyaltyPointsProgressBar/LoyaltyPointsProgressBar";
import styles from "./LoyaltyPointsMenuSection.module.scss";

export const LoyaltyPointsMenuSection = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const addCustomerAnalytics = useCustomerAnalytics();
  const { t, i18n } = useTranslationI18();
  const { openLoyaltyOffersPopup } = useLoyaltyOffersPopupOpener();
  const authApp = useAuth();
  const username = authApp.ReturnAuthenticatedUser()?.name;
  const phoneNumberInfo = useAppSelector(
    (state) => state.userInfo.phoneNumberInfo
  );

  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const selectedFranchise = useAppSelector(
    (state) => state.config.selectedFranchise
  );
  const orderTypeParam = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );
  const bottomSheetModal = useAppSelector(
    (state) => state.globalComponentsToggleSlice.bottomSheetModal
  );
  const pointingSystem = useAppSelector(
    (state) => state.cartAndLoyaltyReducer
  ).pointingSystem;
  const isOfflineLoyaltyScreen = router.pathname.includes(
    PagesEnum.OfflineMode
  );

  const pointsText = t("pointing-system:point");
  const { displayPoints, pointName } = useLoyaltyUiModel(pointsText, i18n);
  const { isEnabled: isPayWithPointsEnabled } = usePayWithPoints("browse");

  /** Package-offer redeemability only — unchanged by loyalty-proxy migration (separate story). */
  const redeemableOffers =
    pointingSystem?.restaurantPackageOffers?.some(
      (restaurantOffer) =>
        restaurantOffer.pointingMilestone?.points! <=
        (pointingSystem?.customerCurrentPoints ?? 0)
    ) ?? false;
  const seeDetailsText = t("pointing-system:see-details");
  const viewIfNotSignInText = t("pointing-system:check-your-total");

  const shouldOpenBottomSheet =
    restaurantInfo &&
    !bottomSheetModal.isOpen &&
    (!selectedFranchise || !orderTypeParam);

  const singleScrollHome = useAppSelector(selectSingleScrollHomeScreen);

  const handleClick = () => {
    addCustomerAnalytics(
      CUSTOMER_ANALYTICS_EVENT.REWARDS_PAGE_FROM_SEE_DETAILS_ABOVE_MILESTONE_METER
    );
    if (singleScrollHome) {
      router.push(getPageLink(router, PagesEnum.Rewards).as);

      return;
    }
    dispatch(updateCurrentHomeScreenTab(TabsEnum.Loyalty));
  };

  const handleRedeem = () => {
    openLoyaltyOffersPopup({
      showPointingMilestonesOnly: true,
    });
  };

  const handleMilestoneBarClick = () => {
    if (shouldOpenBottomSheet) {
      dispatch(setBottomSheetState(true));

      return;
    }
    handleRedeem();
  };

  const isMainDark = restaurantInfo?.colors?.theme === "main_dark";
  const tiersEnabled = useAppSelector(selectGuestTiersEnabled);
  const milestonesEnabled = useAppSelector(selectGuestMilestonesEnabled);
  const hasAnyTierMilestones = useAppSelector(selectHasAnyTierMilestones);
  const customerInfo = useAppSelector(
    (state) => state.loyaltyCustomerInfo.data
  );
  const tierSystem = useAppSelector((state) => state.loyaltyTierSystem.data);
  const tiers = sortLoyaltyTiers(tierSystem?.tiers);
  const currentTier = getCurrentLoyaltyTier(customerInfo, tierSystem);
  const tierColor =
    tiersEnabled && currentTier ? getTierColor(currentTier, tiers) : undefined;

  const handlePayWithPointsClick = () => {
    addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.CLICKED_PAY_POINTS_IN_MENU);
    if (singleScrollHome) {
      router.push(getPageLink(router, PagesEnum.Rewards).as);

      return;
    }
    dispatch(updateCurrentHomeScreenTab(TabsEnum.Loyalty));
  };

  if (isPayWithPointsEnabled && !isOfflineLoyaltyScreen) {
    return (
      <div className={styles.pay_with_points_root}>
        <PayWithPointsBanner
          surface="browse"
          onOpen={handlePayWithPointsClick}
        />
      </div>
    );
  }

  return (
    <div
      className={`${styles.root} ${tiersEnabled ? styles.tiered : ""} ${
        singleScrollHome ? styles.carded : ""
      } ${singleScrollHome && !isOfflineLoyaltyScreen ? styles.tappable : ""}`}
      style={tierColor ? toTierCssProperties(tierColor) : undefined}
      onClick={
        singleScrollHome && !isOfflineLoyaltyScreen ? handleClick : undefined
      }
      data-testid={singleScrollHome ? "points-container" : undefined}
    >
      <div
        className={
          !isOfflineLoyaltyScreen ? styles.confetti : styles.confetti_offline
        }
        data-testid="confetti-div"
      >
        <ConfettiComponent tierTint darkColor={isMainDark} />
      </div>
      <div className={styles.pointing_text} data-testid="pointing-text">
        {phoneNumberInfo.isValid || username?.length > 0 ? (
          // The tier UI shares one balance component with the Rewards tab so the
          // number, unit and points icon line up identically on both.
          tiersEnabled ? (
            <LoyaltyPointsBalance />
          ) : (
            <span className={styles.points_span}>
              <span className={styles.points_number}>
                {`${localizedNumber(displayPoints)}`}
              </span>
              <span className={styles.points_text}>
                {returnSingularOrPlural(pointName, displayPoints)}
              </span>
              <MileStone />
            </span>
          )
        ) : (
          <span className={styles.points_span}>{viewIfNotSignInText}</span>
        )}
        {!isOfflineLoyaltyScreen && (
          <div className={styles.see_details_container}>
            {singleScrollHome ? (
              <button
                type="button"
                className={styles.rewards_arrow}
                aria-label={seeDetailsText}
                data-testid="see-more-points"
              >
                <PointsArrowIcon />
              </button>
            ) : (
              <span
                className={styles.more_text}
                onClick={() => {
                  handleClick();
                }}
                data-testid="see-more-points"
              >
                {seeDetailsText}
              </span>
            )}
          </div>
        )}
      </div>
      <LoyaltyPointsExpiryBanner />

      {/* With no milestones anywhere there is nothing to plot on the meter. */}
      {(!tiersEnabled || (milestonesEnabled && hasAnyTierMilestones)) && (
        <LoyaltyPointsProgressBar
          tierColor={tierColor}
          tierVariant={tiersEnabled}
          onMilestoneBarClick={
            redeemableOffers ? handleMilestoneBarClick : undefined
          }
        />
      )}
    </div>
  );
};
